import { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Shield,
  Network,
  Server,
  Cpu,
  AlertTriangle,
  Camera,
  Zap,
  Lock,
  CheckCircle,
  Users,
  Award,
  Clock,
  Globe,
  MapPin,
  Mail,
  Phone,
  Send,
  Menu,
  X,
  Facebook,
  Instagram,
  Linkedin,
  MessageCircle,
} from 'lucide-react';
import axios from 'axios';
import './App.css';
import ProjectCarousel from './components/ProjectCarousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);
  const heroScale = useTransform(scrollY, [0, 300], [1, 1.1]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setFormStatus({
        type: 'success',
        message: response.data.message,
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus({
        type: 'error',
        message: 'Error al enviar el formulario. Por favor intenta de nuevo.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      icon: Cpu,
      title: 'Consultoría en Informática',
      desc: 'Soluciones tecnológicas personalizadas',
    },
    {
      icon: Network,
      title: 'Redes Inalámbricas y Cableadas',
      desc: 'Diseño e implementación de infraestructura',
    },
    {
      icon: Server,
      title: 'Mantenimiento Correctivo y Preventivo',
      desc: 'Soporte técnico especializado',
    },
    {
      icon: Network,
      title: 'Cableado Estructurado',
      desc: 'Instalación certificada Cat 6/6A',
    },
    {
      icon: Server,
      title: 'Instalación y Configuración de Servidores',
      desc: 'Servidores físicos y virtuales',
    },
    {
      icon: Award,
      title: 'Consultoría y Capacitación en Normas TI',
      desc: 'Estándares internacionales',
    },
    {
      icon: Zap,
      title: 'Automatización de Procesos',
      desc: 'Optimización empresarial',
    },
    {
      icon: CheckCircle,
      title: 'Análisis de Perfil Tecnológico Empresarial',
      desc: 'Diagnóstico integral',
    },
    {
      icon: Zap,
      title: 'Cercas Eléctricas',
      desc: 'Perímetro seguro electrificado',
    },
    {
      icon: AlertTriangle,
      title: 'Alarmas contra Incendios e Intrusión',
      desc: 'Sistemas de detección avanzados',
    },
    {
      icon: Camera,
      title: 'Instalación y Mantenimiento de CCTV',
      desc: 'Videovigilancia HD/4K',
    },
    {
      icon: Server,
      title: 'Venta de Equipos de Cómputo y Accesorios',
      desc: 'Hardware de calidad certificada',
    },
    {
      icon: Lock,
      title: 'Control de Accesos',
      desc: 'Sistemas biométricos y tarjetas',
    },
    {
      icon: Users,
      title: 'Control de Asistencia de Personal',
      desc: 'Registro automatizado',
    },
  ];

  const successCases = [
    {
      title: 'Sistema CCTV Terminal Portuaria',
      client: 'Terminal Portuaria de Limón',
      result: '300+ cámaras con iA instaladas',
    },
    {
      title: 'Red Corporativa Hotel del Caribe',
      client: 'Hotel Puerto Limón',
      result: 'Instalación de red cableada',
    },
    {
      title: 'Control de Acceso en Predio',
      client: 'Transportes ANDREMA',
      result: 'Seguridad biométrica 24/7',
    },
    {
      title: 'Automatización de ingreso Vehicular',
      client: 'Terminal Portuaria de Limón',
      result: '40% reducción tiempo procesos',
    },
  ];

  const whyChooseUs = [
    {
      icon: Zap,
      title: 'Innovación',
      desc: 'Tecnología de punta y soluciones vanguardistas',
    },
    {
      icon: Award,
      title: 'Calidad Certificada',
      desc: 'Estándares internacionales en cada proyecto',
    },
    {
      icon: Clock,
      title: 'Soporte Rápido',
      desc: 'Respuesta inmediata ante cualquier eventualidad',
    },
    {
      icon: Globe,
      title: 'Estándares Internacionales',
      desc: 'Cumplimiento de normas globales de TI',
    },
    {
      icon: MapPin,
      title: 'Experiencia Local',
      desc: 'Conocimiento del mercado costarricense desde 2022',
    },
  ];

  return (
    <div className="bg-control-dark text-white font-inter overflow-x-hidden">
      {/* Navigation */}
      <nav
        data-testid="nav-header"
        className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300
${
  isScrolled
    ? 'bg-control-dark/95 border-b border-white/10 shadow-lg'
    : 'bg-control-dark'
}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-2xl font-outfit font-black tracking-tighter"
              data-testid="nav-logo"
            >
              <img src="/images/logo.png" alt="logo" className="h-60 w-auto" />
            </motion.div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('inicio')}
                className="text-slate-300 hover:text-control-cyan transition-colors"
                data-testid="nav-link-inicio"
              >
                Inicio
              </button>
              <button
                onClick={() => scrollToSection('nosotros')}
                className="text-slate-300 hover:text-control-cyan transition-colors"
                data-testid="nav-link-nosotros"
              >
                Nosotros
              </button>
              <button
                onClick={() => scrollToSection('servicios')}
                className="text-slate-300 hover:text-control-cyan transition-colors"
                data-testid="nav-link-servicios"
              >
                Servicios
              </button>
              <button
                onClick={() => scrollToSection('casos')}
                className="text-slate-300 hover:text-control-cyan transition-colors"
                data-testid="nav-link-casos"
              >
                Casos de Éxito
              </button>
              <button
                onClick={() => scrollToSection('contacto')}
                className="bg-control-cyan text-control-dark font-bold rounded-lg px-6 py-3 hover:bg-white transition-colors"
                data-testid="nav-cta-contact"
              >
                Contáctanos
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white"
              data-testid="nav-mobile-toggle"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden fixed top-20 left-4 right-4 bg-control-dark border border-cyan-400/30 shadow-[0_0_25px_rgba(34,211,238,0.2)] z-[9999] rounded-xl"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-8 space-y-3">
              <button
                onClick={() => scrollToSection('inicio')}
                className="block w-full text-left text-slate-300 hover:text-control-cyan py-2 text-base"
                data-testid="nav-mobile-link-inicio"
              >
                Inicio
              </button>
              <button
                onClick={() => scrollToSection('nosotros')}
                className="block w-full text-left text-slate-300 hover:text-control-cyan py-2 text-base"
                data-testid="nav-mobile-link-nosotros"
              >
                Nosotros
              </button>
              <button
                onClick={() => scrollToSection('servicios')}
                className="block w-full text-left text-slate-300 hover:text-control-cyan py-2 text-base"
                data-testid="nav-mobile-link-servicios"
              >
                Servicios
              </button>
              <button
                onClick={() => scrollToSection('casos')}
                className="block w-full text-left text-slate-300 hover:text-control-cyan py-2 text-base"
                data-testid="nav-mobile-link-casos"
              >
                Casos de Éxito
              </button>
              <button
                onClick={() => scrollToSection('contacto')}
                className="block w-full bg-control-cyan text-control-dark font-bold rounded-lg px-3 py-2 hover:bg-white transition-colors mt-4"
                data-testid="nav-mobile-cta-contact"
              >
                Contáctanos
              </button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section
        id="inicio"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        <motion.div
          style={{ opacity: heroOpacity, scale: heroScale }}
          className="absolute inset-0 z-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                'url(https://static.prod-images.emergentagent.com/jobs/1ddc8c55-6104-4fac-ad64-e7f4043d4efa/images/7a7edda5e9f5a3f71904c62b46ab5a1753538ea96aad12e4cd1816da02ff77a5.png)',
            }}
          />
          <div className="absolute inset-0 bg-black/60" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-5xl sm:text-6xl lg:text-7xl font-outfit font-black tracking-tighter mb-6"
              data-testid="hero-title"
            >
              <span className="text-control-white">CONTROL X</span>
            </motion.h1>
            <motion.h2
              className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold tracking-tight mb-6"
              data-testid="hero-subtitle"
            >
              Expertos en Seguridad Electrónica
            </motion.h2>
            <motion.p
              className="text-base sm:text-lg text-slate-300 mb-8 max-w-2xl mx-auto"
              data-testid="hero-description"
            >
              Fundada en julio de 2022 | Soluciones integrales en TI y Seguridad
              en Limón, Costa Rica
            </motion.p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => scrollToSection('servicios')}
                className="bg-control-cyan text-control-dark font-bold rounded-lg px-8 py-4 hover:bg-white transition-colors focus:ring-4 focus:ring-control-cyan/30"
                data-testid="hero-btn-services"
              >
                Ver Servicios
              </button>
              <button
                onClick={() => scrollToSection('contacto')}
                className="bg-transparent border-2 border-control-cyan text-control-cyan font-bold rounded-lg px-8 py-4 hover:bg-control-cyan/10 transition-colors focus:ring-4 focus:ring-control-cyan/30"
                data-testid="hero-btn-contact"
              >
                Contáctanos
              </button>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-control-cyan rounded-full flex justify-center">
            <div className="w-1 h-3 bg-control-cyan rounded-full mt-2" />
          </div>
        </motion.div>
      </section>
      <ProjectCarousel />

      {/* Sobre Nosotros */}
      <section
        id="nosotros"
        className="py-24 md:py-32 lg:py-40 relative"
        data-testid="about-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-16">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-control-cyan mb-4">
                SOBRE NOSOTROS
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold tracking-tight text-white mb-6">
                Líderes en Tecnología y Seguridad
              </h2>
              <p className="text-base text-slate-300 max-w-3xl mx-auto leading-relaxed">
                Control X es una empresa costarricense especializada en
                seguridad electrónica y tecnologías de la información. Desde
                nuestra fundación en julio de 2022, nos hemos consolidado como
                el aliado estratégico de empresas en Limón y toda Costa Rica,
                ofreciendo soluciones innovadoras y de calidad certificada.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mt-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-control-accent/80 border border-white/10 rounded-xl p-8 hover:border-control-cyan/50 transition-all duration-300"
                data-testid="about-mission-card"
              >
                <div className="flex items-center mb-4">
                  <Shield className="text-control-cyan mr-3" size={32} />
                  <h3 className="text-xl sm:text-2xl font-outfit font-bold text-white">
                    Nuestra Misión
                  </h3>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  Proveer soluciones tecnológicas integrales de seguridad
                  electrónica y TI que protejan los activos de nuestros
                  clientes, optimicen sus operaciones y contribuyan al
                  desarrollo empresarial sostenible en Costa Rica.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-control-accent/30 backdrop-blur-md border border-white/10 rounded-xl p-8 hover:border-control-cyan/50 hover:bg-control-accent/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,191,255,0.15)]"
                data-testid="about-vision-card"
              >
                <div className="flex items-center mb-4">
                  <Zap className="text-control-neon mr-3" size={32} />
                  <h3 className="text-xl sm:text-2xl font-outfit font-bold text-white">
                    Nuestra Visión
                  </h3>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  Ser la empresa líder en seguridad electrónica y servicios de
                  TI en la región Caribe de Costa Rica, reconocidos por nuestra
                  innovación, calidad de servicio y compromiso con la excelencia
                  tecnológica.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Nuestros Servicios */}
      <section
        id="servicios"
        className="py-24 md:py-32 lg:py-40 bg-control-darker relative"
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-control-cyan mb-4">
              NUESTROS SERVICIOS
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold tracking-tight text-white">
              Soluciones Integrales
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-control-accent/30 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:border-control-cyan/50 hover:bg-control-accent/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,191,255,0.15)] hover:-translate-y-2"
                data-testid={`services-card-${index}`}
              >
                <div className="border-t-4 border-control-cyan w-16 mb-4" />
                <service.icon
                  className="text-control-cyan mb-4"
                  size={32}
                  strokeWidth={1.5}
                />
                <h3 className="text-xl font-outfit font-bold text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {service.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Casos de Éxito */}
      <section
        id="casos"
        className="py-24 md:py-32 lg:py-40 relative"
        data-testid="success-cases-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-control-cyan mb-4">
              CASOS DE ÉXITO
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold tracking-tight text-white">
              Proyectos Destacados
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {successCases.map((caso, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="backdrop-blur-2xl bg-control-dark/50 border border-white/10 rounded-2xl shadow-2xl p-6 hover:border-control-cyan/50 transition-all duration-300 hover:-translate-y-2"
                data-testid={`success-case-${index}`}
              >
                <Award className="text-control-cyan mb-4" size={28} />
                <h3 className="text-lg font-outfit font-bold text-white mb-2">
                  {caso.title}
                </h3>
                <p className="text-sm text-slate-400 mb-3">{caso.client}</p>
                <p className="text-control-neon text-sm font-semibold">
                  {caso.result}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Por qué elegirnos */}
      <section
        className="py-24 md:py-32 lg:py-40 bg-control-darker relative"
        data-testid="why-us-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-control-cyan mb-4">
              POR QUÉ ELEGIRNOS
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold tracking-tight text-white">
              Tu Mejor Opción en Seguridad y TI
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-control-accent/30 backdrop-blur-md border border-white/10 rounded-xl p-8 text-center hover:border-control-cyan/50 transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,191,255,0.2)]"
                data-testid={`why-us-card-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-control-cyan/10 mb-4">
                  <item.icon
                    className="text-control-cyan"
                    size={32}
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="text-xl font-outfit font-bold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section
        id="contacto"
        className="py-24 md:py-32 lg:py-40 relative"
        data-testid="contact-section"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-control-cyan mb-4">
              CONTACTO
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold tracking-tight text-white mb-4">
              Conversemos sobre tu proyecto
            </h2>
            <p className="text-slate-300">
              Estamos listos para ayudarte con tus necesidades de seguridad y TI
            </p>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="backdrop-blur-2xl bg-control-dark/50 border border-white/10 rounded-2xl shadow-2xl p-8"
            data-testid="contact-form"
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  Nombre completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleFormChange}
                  className="w-full bg-control-accent/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-control-cyan focus:ring-2 focus:ring-control-cyan/30 outline-none transition-colors"
                  placeholder="Tu nombre"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  Correo electrónico *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleFormChange}
                  className="w-full bg-control-accent/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-control-cyan focus:ring-2 focus:ring-control-cyan/30 outline-none transition-colors"
                  placeholder="tu@email.com"
                  data-testid="contact-input-email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleFormChange}
                  className="w-full bg-control-accent/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-control-cyan focus:ring-2 focus:ring-control-cyan/30 outline-none transition-colors"
                  placeholder="+506 1234-5678"
                  data-testid="contact-input-phone"
                />
              </div>
              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  Empresa (opcional)
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleFormChange}
                  className="w-full bg-control-accent/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-control-cyan focus:ring-2 focus:ring-control-cyan/30 outline-none transition-colors"
                  placeholder="Nombre de tu empresa"
                  data-testid="contact-input-company"
                />
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="message"
                className="block text-sm font-medium text-slate-300 mb-2"
              >
                Mensaje *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                value={formData.message}
                onChange={handleFormChange}
                className="w-full bg-control-accent/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-control-cyan focus:ring-2 focus:ring-control-cyan/30 outline-none transition-colors resize-none"
                placeholder="Cuéntanos sobre tu proyecto..."
                data-testid="contact-input-message"
              />
            </div>

            {formStatus.message && (
              <div
                className={`mb-6 p-4 rounded-lg ${
                  formStatus.type === 'success'
                    ? 'bg-control-neon/10 border border-control-neon/30 text-control-neon'
                    : 'bg-red-500/10 border border-red-500/30 text-red-400'
                }`}
                data-testid="contact-form-status"
              >
                {formStatus.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-control-cyan text-control-dark font-bold rounded-lg px-8 py-4 hover:bg-white transition-colors focus:ring-4 focus:ring-control-cyan/30 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="contact-submit-btn"
            >
              {isSubmitting ? (
                'Enviando...'
              ) : (
                <>
                  <Send className="mr-2" size={20} />
                  Enviar mensaje
                </>
              )}
            </button>
          </motion.form>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="py-20 bg-control-darker border-t border-white/5 relative"
        data-testid="footer"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <h3 className="text-2xl font-outfit font-black mb-4">
                <span className="text-control-cyan">CONTROL</span>
                <span className="text-white"> X</span>
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Seguridad electrónica y soluciones TI de clase mundial en Limón,
                Costa Rica.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-outfit font-bold text-white mb-4">
                Contacto
              </h4>
              <div className="space-y-3">
                <div className="flex items-start text-slate-400 text-sm">
                  <MapPin
                    className="text-control-cyan mr-2 flex-shrink-0"
                    size={18}
                  />
                  <span>Limón, Costa Rica</span>
                </div>
                <div className="flex items-center text-slate-400 text-sm">
                  <Phone className="text-control-cyan mr-2" size={18} />
                  <span>+506 6030-0067</span>
                </div>
                <div className="flex items-center text-slate-400 text-sm">
                  <Mail className="text-control-cyan mr-2" size={18} />
                  <span>info@controlxcr.com</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-outfit font-bold text-white mb-4">
                Síguenos
              </h4>
              <div className="flex space-x-4">
                <a
                  href="https://www.facebook.com/share/1NbLtx66xb/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-control-accent/50 border border-white/10 flex items-center justify-center hover:bg-control-cyan hover:border-control-cyan transition-colors"
                  data-testid="footer-social-facebook"
                  title="Facebook"
                >
                  <Facebook className="text-white" size={18} />
                </a>
                <a
                  href="https://www.instagram.com/controlx.cr/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-control-accent/50 border border-white/10 flex items-center justify-center hover:bg-control-cyan hover:border-control-cyan transition-colors"
                  data-testid="footer-social-instagram"
                  title="Instagram"
                >
                  <Instagram className="text-white" size={18} />
                </a>
                <a
                  href="https://wa.me/50660300067"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-control-neon/20 border border-control-neon/50 flex items-center justify-center hover:bg-control-neon hover:border-control-neon transition-colors"
                  data-testid="footer-social-whatsapp"
                  title="WhatsApp"
                >
                  <MessageCircle
                    className="text-control-neon hover:text-control-dark"
                    size={18}
                    fill="currentColor"
                  />
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Control X. Todos los derechos
              reservados. | Fundada en julio de 2022
            </p>
          </div>
        </div>

        {/* Background text */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-5 pointer-events-none">
          <span className="text-9xl font-outfit font-black text-white">
            CONTROL X
          </span>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/50660300067"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-control-neon rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform animate-pulse"
        data-testid="whatsapp-floating"
        title="Contáctanos por WhatsApp"
      >
        <MessageCircle
          className="text-control-dark"
          size={24}
          fill="currentColor"
        />
      </a>
    </div>
  );
}

export default App;
